// PERMANENT: Dedicated Archive page — ALL published essays, newest first, grouped by month.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Archive as ArchiveIcon } from "lucide-react";
import api from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import ArticleCard from "@/components/ArticleCard";

const ALLOWED_CATEGORIES = ["AI & Books", "Books", "Education", "Learning & Memory", "Mindmaps"];
const NAVY = "#1B2F5E";
const GOLD = "#C9A96E";

function parseDate(a) {
  const raw = a?.published_at || a?.publishedAt || a?.created_at;
  if (!raw) return null;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function monthLabel(ts) {
  return new Date(ts).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export default function ArchivePage() {
  const [active, setActive] = useState("all");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // The categories endpoint only returns categories that have at least one
    // published article — so a whitelisted category with zero essays never
    // gets a tab here.
    api.get("/articles/categories")
      .then(({ data }) => setCategories(Array.isArray(data) ? data.filter((c) => ALLOWED_CATEGORIES.includes(c)) : []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (active !== "all") params.category = active;
    api.get("/articles", { params })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [active]);

  // Backend returns newest first (coalesced published/created date); keep that order.
  const grouped = useMemo(() => {
    const g = new Map();
    for (const a of items) {
      const ts = parseDate(a);
      const key = ts !== null ? monthLabel(ts) : "Undated";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(a);
    }
    return Array.from(g.entries());
  }, [items]);

  const tabs = [{ id: "all", label: "All" }, ...categories.map((c) => ({ id: c, label: c }))];

  return (
    <div className="min-h-screen topo-bg" data-testid="archive-page">
      <SeoHead
        title="The Archive"
        description="Every essay we've published on the SummaryMaps Journal, newest first."
      />

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-16 sm:pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to the journal
        </Link>

        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/60 border border-ink/10 text-xs font-medium mb-4" style={{ color: NAVY }}>
            <ArchiveIcon className="w-3.5 h-3.5" style={{ color: GOLD }} /> The Archive
          </div>
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight"
            style={{ color: NAVY }}
            data-testid="archive-title"
          >
            The Archive
          </h1>
          <p className="mt-4 sm:mt-5 text-base sm:text-lg text-ink-muted leading-relaxed">
            Every essay we've published, newest first.
          </p>
        </div>

        {/* Category filter */}
        <div
          className="mt-10 sm:mt-12 flex items-center justify-center gap-2 flex-wrap"
          data-testid="archive-tabs"
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`whitespace-nowrap px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-medium border transition-all ${
                active === t.id
                  ? "bg-brand text-white border-brand shadow-sm"
                  : "bg-white text-ink-muted border-ink/10 hover:border-brand/40 hover:text-brand"
              }`}
              data-testid={`archive-tab-${t.id.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7 mt-12" data-testid="archive-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-ink/5 overflow-hidden">
                <div className="aspect-[16/10] bg-ink/5 animate-pulse" />
                <div className="p-6 space-y-3">
                  <div className="h-3 w-24 bg-ink/5 rounded animate-pulse" />
                  <div className="h-6 w-3/4 bg-ink/5 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-16 text-center text-ink-muted" data-testid="archive-empty">
            <ArchiveIcon className="w-8 h-8 mx-auto text-ink-subtle/40 mb-3" />
            <p>Nothing here yet — new essays are published several times a week.</p>
          </div>
        ) : (
          <div className="mt-12 sm:mt-14 space-y-14 sm:space-y-16" data-testid="archive-list">
            {grouped.map(([month, arr]) => (
              <section key={month} data-testid={`archive-month-${month.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-baseline gap-4 mb-6 sm:mb-8">
                  <h2
                    className="font-serif text-xs sm:text-sm uppercase tracking-[0.22em] font-semibold"
                    style={{ color: NAVY }}
                  >
                    {month}
                  </h2>
                  <div className="flex-1 h-px" style={{ background: `${GOLD}55` }} />
                  <span className="text-xs uppercase tracking-[0.16em]" style={{ color: "#5C5344" }}>
                    {arr.length} essay{arr.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
                  {arr.map((a) => <ArticleCard key={a.slug} article={a} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

    </div>
  );
}
